import {
  Flex,
  Text,
  useColorModeValue,
  Button,
  FormLabel,
  Input,
  Box,
  Select,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import SearchTable2 from 'components/Tables/SearchTable2';
import React, { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useForm, Controller } from 'react-hook-form';
import { totalRow } from 'state/roles-permissions';
import { useCMSActions } from 'hooks/useCMS';
import DatePickerInput from 'components/CustomDateInput/CustomDateInput';
import { columnsDataRecruitmentSubmission } from 'variables/columnsData';
import { recruitmentSubmissionAtom, recruitmentAtom } from 'state/cms';
import { RxReset } from 'react-icons/rx';
import { BsSearch } from 'react-icons/bs';
import { SUBMISSION_TYPE } from 'variables/general';

function DataTableRecruitmentSubmissions() {
  const textColor = useColorModeValue('gray.700', 'white');
  const listSubmissions = useRecoilValue(recruitmentSubmissionAtom);
  const totalRows = useRecoilValue(totalRow);
  const recruitmentList = useRecoilValue(recruitmentAtom);
  const setRecruitmentSubmissionAtom = useSetRecoilState(recruitmentSubmissionAtom);
  const setTotalRows = useSetRecoilState(totalRow);

  const cmsActions = useCMSActions();
  const [isFetching, setIsFetching] = useState(false);
  const [newListSubmissions, setNewListSubmissions] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [refreshPagingDefault, setRefreshPagingDefault] = useState(false);
  const [recruitmentPositions, setRecruitmentPositions] = useState([]);

  const {
    register: registerSearch,
    handleSubmit: handleSubmitSearch,
    formState: { errors: errorsSearch },
    watch: watchSearch,
    reset: resetSearch,
    control: controlSearch,
  } = useForm();

  useEffect(() => {
    // Load initial data on mount
    const loadInitialData = async () => {
      let pageSize = 10;
      try {
        const s = localStorage && localStorage.getItem('currentPageSizeStore');
        if (s) pageSize = parseInt(s, 10) || 10;
      } catch (e) {}

      const params = {
        name: undefined,
        email: undefined,
        type: undefined,
        position_id: undefined,
        date_from: undefined,
        date_to: undefined,
        sort_type: '',
        sort_field: '',
        offset: 0,
        limit: pageSize,
      };

      await cmsActions.getListRecruitmentSubmission(params, 0, pageSize);
      setHasSearched(true);
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    // Fetch recruitment positions for filter dropdown
    const fetchRecruitmentPositions = async () => {
      try {
        await cmsActions.getListRecruitment({ status: 1 }, 0, 100);
      } catch (error) {
        console.error('Error fetching recruitment positions:', error);
      }
    };

    fetchRecruitmentPositions();
  }, []);

  useEffect(() => {
    // Update recruitment positions from recoil atom
    if (recruitmentList && recruitmentList.length > 0) {
      setRecruitmentPositions(recruitmentList);
    }
  }, [recruitmentList]);

  useEffect(() => {
    if (listSubmissions && listSubmissions.length > 0) {
      const submissions = listSubmissions.map((i) => {
        const typeItem = SUBMISSION_TYPE.find((t) => t.id === String(i.type));
        return {
          ...i,
          type_name: typeItem ? typeItem.name : '',
          filters: {
            name: watchSearch('nameSearch') || undefined,
            email: watchSearch('emailSearch') || undefined,
            type: watchSearch('typeSearch') || undefined,
            position_id: watchSearch('positionSearch') || undefined,
            date_from: watchSearch('date_from')
              ? new Date(new Date(watchSearch('date_from')).setHours(0, 0, 0, 0)).toISOString()
              : undefined,
            date_to: watchSearch('date_to')
              ? new Date(new Date(watchSearch('date_to')).setHours(23, 59, 59, 999)).toISOString()
              : undefined,
          },
        };
      });
      setNewListSubmissions(submissions);
    } else {
      // Reset table when data is null or empty
      setNewListSubmissions([]);
    }
  }, [
    listSubmissions,
    watchSearch('nameSearch'),
    watchSearch('emailSearch'),
    watchSearch('typeSearch'),
    watchSearch('positionSearch'),
    watchSearch('date_from'),
    watchSearch('date_to'),
  ]);

  const onSubmitSearch = async (formValues) => {
    let pageSize = 10;
    try {
      const s = localStorage && localStorage.getItem('currentPageSizeStore');
      if (s) pageSize = parseInt(s, 10) || 10;
    } catch (e) {}

    const params = {
      name: formValues.nameSearch || undefined,
      email: formValues.emailSearch || undefined,
      type: formValues.typeSearch || undefined,
      position_id: formValues.positionSearch || undefined,
      date_from: formValues.date_from
        ? new Date(new Date(formValues.date_from).setHours(0, 0, 0, 0)).toISOString()
        : undefined,
      date_to: formValues.date_to
        ? new Date(new Date(formValues.date_to).setHours(23, 59, 59, 999)).toISOString()
        : undefined,
      sort_type: '',
      sort_field: '',
      offset: 0,
      limit: pageSize,
    };

    await cmsActions.getListRecruitmentSubmission(params, 0, pageSize);
    setHasSearched(true);
    setRefreshPagingDefault(true);
    setTimeout(() => {
      setRefreshPagingDefault(false);
    }, 100);
  };

  const handleResetSearch = () => {
    resetSearch({
      nameSearch: '',
      emailSearch: '',
      typeSearch: '',
      positionSearch: '',
      date_from: null,
      date_to: null,
    });
    setHasSearched(false);
  };

  const recallApi = async (fetchDataOptions, isFromSearch) => {
    setIsFetching(true);
    const pageIndex = fetchDataOptions?.pageIndex ?? 0;
    const pageSize = fetchDataOptions?.pageSize ?? 10;
    const params = {
      name: watchSearch('nameSearch') || undefined,
      email: watchSearch('emailSearch') || undefined,
      type: watchSearch('typeSearch') || undefined,
      position_id: watchSearch('positionSearch') || undefined,
      date_from: watchSearch('date_from')
        ? new Date(new Date(watchSearch('date_from')).setHours(0, 0, 0, 0)).toISOString()
        : undefined,
      date_to: watchSearch('date_to')
        ? new Date(new Date(watchSearch('date_to')).setHours(23, 59, 59, 999)).toISOString()
        : undefined,
      sort_type: fetchDataOptions?.sort_type || '',
      sort_field: fetchDataOptions?.sort_field || '',
      offset: pageIndex * pageSize,
      limit: pageSize,
    };

    if (hasSearched || isFromSearch) {
      await cmsActions.getListRecruitmentSubmission(params, pageIndex, pageSize);
    }
    setIsFetching(false);
    if (isFromSearch) {
      setHasSearched(true);
    }
  };

  return (
    <Card px='0px'>
      <CardHeader px='22px' mb='12px'>
        <Flex direction='column' w='100%'>
          <Text
            color={textColor}
            fontSize='lg'
            fontWeight='bold'
            mb='6px'
          >
            Submission Management
          </Text>
        </Flex>
      </CardHeader>
      <CardBody overflowX={{ sm: 'scroll', lg: 'hidden' }} px={0}>
        <Card px='0px' mb={4} boxShadow='none'>
          <CardBody px={4}>
            <form onSubmit={handleSubmitSearch(onSubmitSearch)}>
              <Flex
                direction='row'
                alignItems='center'
                mb={3}
                gap={4}
                flexWrap={'wrap'}
              >
                <Box direction='row' alignItems='center' display='block' minW='250px'>
                  <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                    Name
                  </FormLabel>
                  <Input
                    ms='4px'
                    type='text'
                    size='sm'
                    placeholder='Search by name'
                    name='nameSearch'
                    {...registerSearch('nameSearch')}
                  />
                </Box>

                <Box direction='row' alignItems='center' display='block' minW='250px'>
                  <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                    Email
                  </FormLabel>
                  <Input
                    ms='4px'
                    type='text'
                    size='sm'
                    placeholder='Search by email'
                    name='emailSearch'
                    {...registerSearch('emailSearch')}
                  />
                </Box>

                <Box direction='row' alignItems='center' display='block' minW='180px'>
                  <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                    Submission Type
                  </FormLabel>
                  <Select
                    ms='4px'
                    type='text'
                    size='sm'
                    name='typeSearch'
                    {...registerSearch('typeSearch')}
                  >
                    {SUBMISSION_TYPE.map((item) => (
                      <option key={item.id} value={item.id === '0' ? '' : item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                </Box>

                <Box direction='row' alignItems='center' display='block' minW='250px'>
                  <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                    Position
                  </FormLabel>
                  <Select
                    ms='4px'
                    type='text'
                    size='sm'
                    name='positionSearch'
                    {...registerSearch('positionSearch')}
                  >
                    <option value=''>All positions</option>
                    {recruitmentPositions.map((position) => (
                      <option key={position.id} value={position.id}>
                        {position.position}
                      </option>
                    ))}
                  </Select>
                </Box>

                <Box direction='row' alignItems='center' display='block'>
                  <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                    Date From
                  </FormLabel>
                  <Controller
                    name='date_from'
                    control={controlSearch}
                    render={({ field }) => (
                      <DatePickerInput
                        onChange={(value) => field.onChange(value)}
                        selected={watchSearch('date_from')}
                        dateFormat='dd/MM/yyyy'
                        placeholderText='Select date from'
                        isShowMinDate={true}
                      />
                    )}
                  />
                </Box>

                <Box direction='row' alignItems='center' display='block'>
                  <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                    Date To
                  </FormLabel>
                  <Controller
                    name='date_to'
                    control={controlSearch}
                    render={({ field }) => (
                      <DatePickerInput
                        onChange={(value) => field.onChange(value)}
                        selected={watchSearch('date_to')}
                        dateFormat='dd/MM/yyyy'
                        placeholderText='Select date to'
                        showTimeSelect={false}
                        startDate={watchSearch('date_from')}
                        minDate={watchSearch('date_from')}
                        selectsEnd
                      />
                    )}
                  />
                </Box>
              </Flex>

              <Flex justify='center' align='center' gap='8px' mt={3}>
                <Button
                  leftIcon={<RxReset />}
                  variant='primary'
                  w='88px'
                  h='33px'
                  onClick={handleResetSearch}
                >
                  Reset
                </Button>
                <Button
                  leftIcon={<BsSearch />}
                  variant='primary'
                  w='88px'
                  h='33px'
                  type='submit'
                >
                  Search
                </Button>
              </Flex>
            </form>
          </CardBody>
        </Card>

        <SearchTable2
          tableData={newListSubmissions}
          columnsData={columnsDataRecruitmentSubmission}
          totalRow={totalRows.recruitmentSubmissions || 0}
          loadData={(fetchDataOptions) => recallApi(fetchDataOptions, false)}
          isFetching={isFetching}
          refreshPagingDefault={refreshPagingDefault}
          enableRowActions={true}
        />
      </CardBody>
    </Card>
  );
}

export default DataTableRecruitmentSubmissions;
